import {useState, useEffect, useRef, Fragment} from 'react'
import {useTranslation} from 'next-i18next'
import {useRouter} from 'next/router'
import {dayjs} from 'src/utils/date'
import {makeStyles} from '@material-ui/core/styles'
import AppDivider from 'src/components/elements/AppDivider'
import AppRoundedButton from 'src/components/elements/buttons/AppRoundedButton'
import AppTypography from 'src/components/elements/typography/AppTypography'
import UniquePropertyDate from 'src/components/modules/property/singlePage/propertyHeader/UniquePropertyDate'
import UniquePropertyGuest from 'src/components/modules/property/singlePage/propertyHeader/UniquePropertyGuest'
import WarningMessage from 'src/components/elements/helpers/WarningMessage'
import BookingPriceCalculations from 'src/components/modules/booking/prices/priceCalculations/BookingPriceCalculations'
import StarRating from 'src/components/elements/StarRating'
import {useCalendarContext} from 'src/context/CalendarProvider'
import {publicCheckBookingAvailableApi} from 'src/services/api/booking'

import {useReviewInfo} from 'src/hooks/property'
import {GuestSelectType} from 'src/types/guestSelect'
import {PropertyType} from 'src/types/property'
import {handleHttpException} from 'src/services/http'
import {useCurrentLocale} from 'src/hooks/locale'
import {ADMIN_GUEST_MESSAGES_PAGE, ADMIN_INBOX_PAGE} from 'src/constants/route'
import {useIsAdmin} from 'src/hooks/user'
import clsx from 'clsx'
import Hidden from 'src/components/helpers/Hidden'
import {SelectDateType} from 'src/types/utils'
import Visible from 'src/components/helpers/Visible'
import {getCurrencySymbol, tryAsyncFunc} from 'src/utils/other'
import {BookingPricesType} from 'src/types/booking'
import MCAdvantages from 'src/components/modules/booking/components/MCAdvantages'
import PropertyPromotionContent from 'src/components/modules/property/promo/PropertyPromotionContent'

//todo convert to styles.module.css or tailwind utility classes
const useStyles = makeStyles(({spacing, palette}) => ({
  cardItem: {
    display: 'flex',
    flexDirection: 'column',
    //height: '100%',
    borderRadius: '0.75rem',
    paddingTop: spacing(3),
    paddingBottom: spacing(1.5),
    paddingLeft: spacing(3),
    paddingRight: spacing(3),
    border: `1px solid ${palette.grey[200]}`,
    height: 'fit-content', //min-height is the content
    //maxHeight: 'calc(100vh - 132px)', //fill up screen + 120px + 12px padding
  },
  reviewMargin: {
    display: 'flex',
    margin: spacing(2.25, 0),
  },
  bookingBtn: {
    width: '100%',
    padding: spacing(1.5, 0),
    margin: spacing(2.5, 0),
  },
  disabled: {
    background: `${palette.grey[200]} !important`,
    color: `${palette.grey[500]} !important`,
    cursor: 'default',
  },
}))

export default function Booking(props: {
  property: PropertyType
  selectedDates: SelectDateType
  setSelectedDates: (dates: SelectDateType) => void
  guests: GuestSelectType
  setGuests: (guests: GuestSelectType) => void
  handleBooking: (prices: BookingPricesType | null) => void
  enabled: boolean
  trigger: 'auto' | 'manual'
}) {
  const {
    property,
    selectedDates,
    setSelectedDates,
    guests,
    setGuests,
    handleBooking,
    enabled,
    trigger,
  } = props

  const isAdmin = useIsAdmin()
  const router = useRouter()
  const classes = useStyles()
  const {t} = useTranslation(['common', 'booking'])
  const currentLocale = useCurrentLocale()
  const currencySymbol = getCurrencySymbol(property.basic_pricing?.currency)

  const containerRef = useRef<HTMLDivElement | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [prices, setPrices] = useState<BookingPricesType | null>(null)
  const [error, setError] = useState<{
    message: string | null
    booking_uid: string
  } | null>(null)

  const handleSetPrices = (newPrices: BookingPricesType | null) => {
    setPrices(newPrices)

    //only scroll if its the user that search
    if (!prices && newPrices && trigger === 'manual') {
      const pos = containerRef.current?.getBoundingClientRect()
      if (!pos) {
        return
      }

      window.scrollTo({
        top: pos.top + window.scrollY - 70, //70 = header space
        left: 0,
        // behavior: 'smooth',
      })
    }
  }

  const checkBookingAvailable = async () => {
    if (!selectedDates[0]) return
    if (!selectedDates[1]) return
    if (!property) return
    if (!guests) return
    if (!guests.adults) return

    setError(null)
    setLoading(true)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [res, err] = await tryAsyncFunc(
      publicCheckBookingAvailableApi(property.id, {
        start: dayjs(selectedDates[0]).format('YYYY-M-D'),
        end: dayjs(selectedDates[1]).format('YYYY-M-D'),
        options: guests,
        locale: currentLocale,
        user_id: null,
      }),
    )
    setLoading(false)
    if (err) {
      handleHttpException(
        (message) => {
          if (!message) {
            setError(null)
            return
          }

          setError({
            message: message,
            booking_uid: err?.response?.data?.booking_uid || '',
          })
        },
        t,
        'booking',
      )(err)
    }
  }

  const onBookingRequest = () => {
    if (!guests || !selectedDates || !enabled || loading) {
      setError({
        message: t('booking_no_dates_no_guests', {ns: 'booking'}),
        booking_uid: '',
      })
      return
    }

    setError(null)
    setLoading(true)
    publicCheckBookingAvailableApi(property.id, {
      start: dayjs(selectedDates[0]).format('YYYY-M-D'),
      end: dayjs(selectedDates[1]).format('YYYY-M-D'),
      options: guests,
      locale: currentLocale,
      user_id: null,
    })
      .then(() => {
        handleBooking(prices)
      })
      .catch((err) => {
        handleHttpException(
          (message) => {
            if (!message) {
              setError(null)
              return
            }

            setError({
              message: message,
              booking_uid: err?.response?.data?.booking_uid || '',
            })
          },
          t,
          'booking',
        )(err)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    setError(null)
    checkBookingAvailable()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDates, guests])

  const externalBookingBtn: boolean =
    property.should_redirect_for_booking && !!property.book_now_url

  const lowestRate =
    property.basic_pricing?.calculated_lowest_rate ||
    property.basic_pricing?.base_rate

  return (
    <div
      ref={containerRef}
      className={clsx(prices ? '' : 'sticky top-[120px]')}
    >
      <PropertyPromotionContent className="mb-6" property={property} />
      <div className={classes.cardItem}>
        <Visible when={externalBookingBtn}>
          <Fragment>
            <AppTypography
              variant="caption"
              neutralColor={400}
              className="text-center"
            >
              {t('booking_external_url')}
            </AppTypography>
            {/* eslint-disable-next-line react/jsx-no-target-blank */}
            <a
              href={property.book_now_url as string}
              rel="nofollow"
              target="_blank"
              style={{textDecoration: 'none'}}
            >
              <AppRoundedButton
                className={classes.bookingBtn}
                variant="contained"
                color="primary"
                id="book_now"
              >
                {t('booking_request')}
              </AppRoundedButton>
            </a>
          </Fragment>
        </Visible>
        <Visible when={!externalBookingBtn}>
          <Fragment>
            <AppTypography variant="displaySmall" neutralColor={500}>
              {t('From')}
            </AppTypography>
            <div className="flex flex-row justify-start items-end gap-1 mb-4">
              <AppTypography variant="displayLarge" neutralColor={800}>
                {currencySymbol}
                {lowestRate}
              </AppTypography>
              <AppTypography variant="body" neutralColor={500}>
                {`/${t('night')}`}
              </AppTypography>
            </div>
            <Hidden when>
              <RatingSection property={property} />
            </Hidden>
            <AppDivider />
            <CalendarSection
              property={property}
              selectedDates={selectedDates}
              setSelectedDates={setSelectedDates}
            />
            <GuestsSection
              property={property}
              guests={guests}
              onChange={setGuests}
            />
            <WarningMessage
              message={error?.message}
              actionOnClick={() => {
                if (!error?.booking_uid) {
                  return
                }

                router.push(
                  `${
                    isAdmin ? ADMIN_INBOX_PAGE : ADMIN_GUEST_MESSAGES_PAGE
                  }?booking_uid=${error.booking_uid}`,
                )
              }}
              actionLabel={
                error?.booking_uid
                  ? t('Click here to access it', {ns: 'booking'})
                  : undefined
              }
            />
            <BookingButton
              disabled={!enabled || loading || !!error}
              onBookingRequest={onBookingRequest}
            />
            <AppTypography
              variant="caption"
              neutralColor={500}
              className="text-center"
              dangerouslySetInnerHTML={{
                __html: t('booking_request_description'),
              }}
            />
            <br />
            <Visible when={!!prices}>
              <>
                <AppDivider />
                <br />
              </>
            </Visible>
            <BookingPriceCalculations
              property={property}
              start={selectedDates[0]}
              end={selectedDates[1]}
              guestsInfo={guests}
              setPrices={handleSetPrices}
              onError={(err) => {
                if (!err) return
                setError({message: err, booking_uid: ''})
              }}
            />
          </Fragment>
        </Visible>
      </div>
      <div className="mt-6">
        <MCAdvantages />
      </div>
    </div>
  )
}

function BookingButton(props: {
  disabled: boolean
  onBookingRequest: () => void
}) {
  const {onBookingRequest, disabled} = props
  const {t} = useTranslation('common')
  const classes = useStyles()
  const classNames = clsx(classes.bookingBtn, {[classes.disabled]: disabled})

  return (
    <AppRoundedButton
      className={classNames}
      onClick={onBookingRequest}
      variant="contained"
      color="primary"
      id="book_now"
    >
      {t('booking_request')}
    </AppRoundedButton>
  )
}

function RatingSection(props: {property: PropertyType}) {
  const {property} = props

  const classes = useStyles()
  const reviewInfo = useReviewInfo(property.review_count)

  if (!property.review_display || !property.average_rating) {
    return null
  }

  return (
    <div className={classes.reviewMargin}>
      <div className="mr-3">
        <StarRating value={property.average_rating} readOnly />
      </div>
      <div className="flex flex-row justify-start items-start gap-2">
        <AppTypography variant="action" neutralColor={800}>
          {property.average_rating.toFixed(2)}
        </AppTypography>
        <AppTypography variant="body" neutralColor={500}>
          {reviewInfo}
        </AppTypography>
      </div>
    </div>
  )
}

export function CalendarSection(props: {
  property: PropertyType
  selectedDates: SelectDateType
  setSelectedDates: (dates: SelectDateType) => void
}) {
  const {property, selectedDates, setSelectedDates} = props

  const {datesStatusMap} = useCalendarContext()

  if (!property.calendar_display) {
    return null
  }

  return (
    <div className="mt-6">
      <UniquePropertyDate
        property={property}
        selectedDates={selectedDates}
        datesStatusMap={datesStatusMap}
        disableFlexibleDates
        setSelectedDates={setSelectedDates}
      />
    </div>
  )
}

export function GuestsSection(props: {
  property: PropertyType
  guests: GuestSelectType
  onChange: (guests: GuestSelectType) => void
}) {
  const {property, guests, onChange} = props

  if (!property.guests_display) {
    return null
  }

  return (
    <div className="my-6">
      <UniquePropertyGuest
        rules={property.rules}
        guests={guests}
        maximumGuests={property.maximum_guests}
        maximumAdults={property.maximum_adults}
        maximumAnimals={property.basic_fee?.maximum_animals}
        onChange={onChange}
      />
    </div>
  )
}
